import axios from 'axios';
import client from './index';
import { BASE_URL } from './index';

// Types
export interface TranslatableField {
  [languageCode: string]: string;
}

export interface MenuItemResponse {
  id: string;
  category_id: string;
  name: TranslatableField;
  description: TranslatableField | null;
  price: number;
  image_url: string | null;
  badges: string[];
  spicy_level: number | null;
  options: MenuItemOption[] | null;
  is_visible: boolean;
  is_sold_out: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CategoryResponse {
  id: string;
  menu_id: string;
  name: TranslatableField;
  description: TranslatableField | null;
  image_url: string | null;
  is_visible: boolean;
  display_order: number;
  items: MenuItemResponse[];
  created_at: string;
  updated_at: string;
}

export interface StoreInfo {
  store_hours?: string | null;
  address?: string | null;
  address_url?: string | null;
  wifi_password?: string | null;
  instagram?: string | null;
}

export interface MenuItemOption {
  name: TranslatableField;
  price: number;
}

export interface MenuTheme {
  background_color: string;
  text_color: string;
  secondary_text_color: string;
  accent_color: string;
  border_color: string;
  price_color: string;
}

export const DEFAULT_MENU_THEME: MenuTheme = {
  background_color: '#FFFFFF',
  text_color: '#111111',
  secondary_text_color: '#666666',
  accent_color: '#111111',
  border_color: '#EEEEEE',
  price_color: '#111111',
};

export interface BadgeDefinition {
  key: string;
  label: TranslatableField;
  color: string;
}

export interface MenuResponse {
  id: string;
  business_id: string;
  name: TranslatableField;
  slug: string;
  description: TranslatableField | null;
  currency: string;
  available_languages: string[];
  default_language: string;
  is_active: boolean;
  theme: MenuTheme | null;
  store_info: StoreInfo | null;
  header_image_url: string | null;
  badge_definitions?: BadgeDefinition[];
  categories?: CategoryResponse[];
  created_at: string;
  updated_at: string;
}

export interface CreateMenuRequest {
  business_id: string;
  name?: TranslatableField;
  description?: TranslatableField;
  currency?: string;
  available_languages?: string[];
  default_language?: string;
  is_active?: boolean;
}

export interface UpdateMenuRequest {
  name?: TranslatableField;
  slug?: string;
  description?: TranslatableField;
  currency?: string;
  available_languages?: string[];
  default_language?: string;
  is_active?: boolean;
  theme?: MenuTheme;
  store_info?: StoreInfo;
}

export interface CreateCategoryRequest {
  name: TranslatableField;
  description?: TranslatableField;
  is_visible?: boolean;
}

export interface UpdateCategoryRequest {
  name?: TranslatableField;
  description?: TranslatableField;
  image_url?: string | null;
  is_visible?: boolean;
}

export interface CreateItemRequest {
  name: TranslatableField;
  description?: TranslatableField;
  price?: number;
  badges?: string[];
  spicy_level?: number | null;
  options?: MenuItemOption[] | null;
  is_visible?: boolean;
  is_sold_out?: boolean;
}

export interface UpdateItemRequest {
  name?: TranslatableField;
  description?: TranslatableField;
  price?: number;
  badges?: string[];
  spicy_level?: number | null;
  options?: MenuItemOption[] | null;
  is_visible?: boolean;
  is_sold_out?: boolean;
  image_url?: string | null;
}

// Menu CRUD

export const getMenus = async (businessId: string): Promise<MenuResponse[]> => {
  const response = await client.get<MenuResponse[]>(`/menus?business_id=${businessId}`);
  return response.data;
};

export const getMenu = async (menuId: string): Promise<MenuResponse> => {
  const response = await client.get<MenuResponse>(`/menus/${menuId}`);
  return response.data;
};

export const createMenu = async (data: CreateMenuRequest): Promise<MenuResponse> => {
  const response = await client.post<MenuResponse>('/menus', data);
  return response.data;
};

export const updateMenu = async (menuId: string, data: UpdateMenuRequest): Promise<MenuResponse> => {
  const response = await client.patch<MenuResponse>(`/menus/${menuId}`, data);
  return response.data;
};

export const deleteMenu = async (menuId: string): Promise<void> => {
  await client.delete(`/menus/${menuId}`);
};

// Category CRUD

export const createCategory = async (menuId: string, data: CreateCategoryRequest): Promise<CategoryResponse> => {
  const response = await client.post<CategoryResponse>(`/menus/${menuId}/categories`, data);
  return response.data;
};

export const updateCategory = async (menuId: string, categoryId: string, data: UpdateCategoryRequest): Promise<CategoryResponse> => {
  const response = await client.patch<CategoryResponse>(`/menus/${menuId}/categories/${categoryId}`, data);
  return response.data;
};

export const deleteCategory = async (menuId: string, categoryId: string): Promise<void> => {
  await client.delete(`/menus/${menuId}/categories/${categoryId}`);
};

export const reorderCategories = async (menuId: string, orderedIds: string[]): Promise<void> => {
  await client.put(`/menus/${menuId}/categories/reorder`, { ordered_ids: orderedIds });
};

// Item CRUD

export const createItem = async (menuId: string, categoryId: string, data: CreateItemRequest): Promise<MenuItemResponse> => {
  const response = await client.post<MenuItemResponse>(`/menus/${menuId}/categories/${categoryId}/items`, data);
  return response.data;
};

export const updateItem = async (itemId: string, data: UpdateItemRequest): Promise<MenuItemResponse> => {
  const response = await client.patch<MenuItemResponse>(`/menus/items/${itemId}`, data);
  return response.data;
};

export const deleteItem = async (itemId: string): Promise<void> => {
  await client.delete(`/menus/items/${itemId}`);
};

export const reorderItems = async (categoryId: string, orderedIds: string[]): Promise<void> => {
  await client.put(`/menus/categories/${categoryId}/items/reorder`, { ordered_ids: orderedIds });
};

// Item Image

export const uploadItemImage = async (itemId: string, file: File | Blob): Promise<MenuItemResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<MenuItemResponse>(`/menus/items/${itemId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return response.data;
};

// Category Image

export const uploadCategoryImage = async (menuId: string, categoryId: string, file: File | Blob): Promise<CategoryResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<CategoryResponse>(`/menus/${menuId}/categories/${categoryId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return response.data;
};

// Menu Header Image

export const uploadMenuHeaderImage = async (menuId: string, file: File | Blob): Promise<MenuResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<MenuResponse>(`/menus/${menuId}/header-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return response.data;
};

export const deleteMenuHeaderImage = async (menuId: string): Promise<MenuResponse> => {
  const response = await client.delete<MenuResponse>(`/menus/${menuId}/header-image`);
  return response.data;
};

// Public Menu (No Auth)

export interface PublicBadgeDefinition {
  key: string;
  label: string;
  color: string;
}

export interface PublicMenuItemOption {
  name: string;
  price: number;
}

export interface PublicMenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  badges: string[];
  spicy_level: number | null;
  options: PublicMenuItemOption[] | null;
  is_sold_out: boolean;
}

export interface PublicCategory {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  items: PublicMenuItem[];
}

export interface PublicMenuResponse {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  currency: string;
  available_languages: string[];
  language: string;
  theme: MenuTheme | null;
  store_info: StoreInfo | null;
  header_image_url: string | null;
  badge_definitions: PublicBadgeDefinition[];
  categories: PublicCategory[];
}

const publicClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

export const getPublicMenu = async (slug: string, lang?: string): Promise<PublicMenuResponse> => {
  const params = lang ? { lang } : {};
  const response = await publicClient.get<PublicMenuResponse>(`/public/menu/${slug}`, { params });
  return response.data;
};

// Badge Definitions

export const getBadgeDefinitions = async (menuId: string): Promise<BadgeDefinition[]> => {
  const response = await client.get<BadgeDefinition[]>(`/menus/${menuId}/badges`);
  return response.data;
};

export const replaceBadgeDefinitions = async (menuId: string, badges: BadgeDefinition[]): Promise<BadgeDefinition[]> => {
  const response = await client.put<BadgeDefinition[]>(`/menus/${menuId}/badges`, { badges });
  return response.data;
};

export const addBadgeDefinition = async (menuId: string, badge: BadgeDefinition): Promise<BadgeDefinition[]> => {
  const response = await client.post<BadgeDefinition[]>(`/menus/${menuId}/badges`, badge);
  return response.data;
};

export const deleteBadgeDefinition = async (menuId: string, badgeKey: string): Promise<void> => {
  await client.delete(`/menus/${menuId}/badges/${badgeKey}`);
};

// Background Tasks

export interface ScrapeMenuRequest {
  url: string;
  limit?: number;
}

export interface TaskResponse {
  task_id: string;
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE';
  result?: {
    status: string;
    menu_id?: string;
    slug?: string;
    categories?: number;
    items?: number;
    warnings?: string[];
    error?: string;
  };
}

export const scrapeMenu = async (data: ScrapeMenuRequest): Promise<TaskResponse> => {
  const response = await client.post<TaskResponse>('/menus/scrape', data);
  return response.data;
};

export const getTaskStatus = async (taskId: string): Promise<TaskResponse> => {
  const response = await client.get<TaskResponse>(`/menus/tasks/${taskId}`);
  return response.data;
};

export const duplicateMenu = async (menuId: string): Promise<TaskResponse> => {
  const response = await client.post<TaskResponse>(`/menus/${menuId}/duplicate`);
  return response.data;
};

// Slug Availability

export interface SlugCheckResponse {
  available: boolean;
  slug: string;
}

export const checkSlugAvailability = async (slug: string, menuId?: string): Promise<SlugCheckResponse> => {
  const params: Record<string, string> = { slug };
  if (menuId) params.menu_id = menuId;
  const response = await client.get<SlugCheckResponse>('/menus/check-slug', { params });
  return response.data;
};

export interface TranslateFieldsRequest {
  fields: { [key: string]: string };
  target_languages?: string[];
}

export interface TranslateFieldsResponse {
  translations: { [lang: string]: { [field: string]: string } };
}

export const translateFields = async (menuId: string, data: TranslateFieldsRequest): Promise<TranslateFieldsResponse> => {
  const response = await client.post<TranslateFieldsResponse>(`/menus/${menuId}/translate-fields`, data);
  return response.data;
};
